import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import SaveIcon from '@mui/icons-material/Save';
import { forEachSeries } from 'p-iteration';
import { TimePicker } from '@mui/lab';
import moment from 'moment';
import PropTypes from 'prop-types';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useLogger from '../../../../../hooks/useLogger';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import CoachConfig from '../../../../Model/CoachConfig';
import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import { CoachingActivity } from '../../../../../utils/log';
import { parseTimeString } from '../../../../../utils/time';
import Select from '../../../../../components/Select';
import InputLabel from '../../../../../components/v2/InputLabel';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import { SaveButton } from '../../../../../components/Button/ActionButtons';

import {
  Title,
  Description,
} from '../../styles';

import {
  ContentWrapper,
  StyledTextField,
  InputContainer,
  StyledTimeField,
  StyledWrapper,
  ButtonContainer,
} from './styles';
import texts from './texts.json';
import WeeklyMessageAttachmentContainer from './components/AttachmentContainer';

const MAX_CHARACTERS_COUNT = 5000;

const ToggleOption = {
  ENABLE: 'ENABLE',
  DISABLE: 'DISABLE',
};

const ClientWeeklyJourneyMessagingToggleOptions = Object.entries(ToggleOption).map(([key, value]) => ({
  value,
  label: texts[value],
  key,
}));

const ClientWeeklyJourneyMessaging = ({
  cohortOptions,
  dayOptions,
}) => {
  const [coachConfig, setCoachConfig] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(cohortOptions[0].value);
  const [message, setMessage] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [messageSendDay, setMessageSendDay] = useState(2);
  const [messageSendTime, setMessageSendTime] = useState(new Date().setHours(9, 0, 0, 0));
  const [fileAttachments, setFileAttachments] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const { userId } = useContext(LoggedInUserContext);
  const {
    uploadAttachments,
    deleteAttachments,
    getBlobUrl,
  } = useStorage();
  const { logCoachingActivity } = useLogger();

  const { params: { userId: coachId } } = useRouteMatch();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      setIsReady(false);
      const coachConfigDoc = await CoachConfig.getCoachConfigByCoachId(coachId);
      const selectedWeekConfig = coachConfigDoc?.clientJourneyWeeklyMessaging.find(
        ({ week }) => week === selectedWeek,
      );

      const files = [];
      await forEachSeries((selectedWeekConfig?.attachments || []), async (attachment) => {
        const dataUrl = await getBlobUrl(attachment.url);
        files.push({
          ...attachment,
          dataUrl,
          isUploaded: true,
        });
      });

      if (isComponentMountedRef.current) {
        setCoachConfig(coachConfigDoc);
        const config = selectedWeekConfig || {
          message: '',
          isEnabled: false,
          messageSendDay: 2,
          messageSendTime: '09:00',
        };

        setMessage(config.message);
        setCharacterCount(config.message.length);
        setFileAttachments(selectedWeekConfig ? files : []);
        setIsEnabled(config.isEnabled);
        setMessageSendDay(config.messageSendDay);

        const [hours, minutes] = parseTimeString(config.messageSendTime);
        const sendTime = new Date();
        sendTime.setHours(hours, minutes, 0, 0);
        setMessageSendTime(sendTime);

        setIsReady(true);
      }
    };
    init();
  }, [
    coachId,
    selectedWeek,
    isComponentMountedRef,
    getBlobUrl,
  ]);

  const handleMessageTextChange = useCallback((event) => {
    const newValue = event.target.value;
    setMessage(newValue);
    setCharacterCount(newValue.length);
  }, []);

  const uploadMessageAttachments = useCallback(async () => {
    // adding already uploaded files to the array
    const attachmentsRefs = fileAttachments.reduce((arr, current) => {
      if (current.isUploaded) {
        arr.push({
          name: current.name,
          url: current.url,
        });
      }
      return arr;
    }, []);

    // filtering files that are not uploaded yet
    const filesToUpload = fileAttachments.filter((file) => !file.isUploaded);
    const storagePath = format(storagePaths.CLIENT_JOURNEY_WEEKLY_MESSAGE_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: coachId,
      week: `Week${selectedWeek}`,
    });

    let storageTaskFailed = false;
    let errorMsg = '';
    setIsUploading(true);
    try {
      const result = await uploadAttachments(filesToUpload, storagePath);

      const successfulUploads = [];
      result.forEach((res, index) => {
        if (res.state === StorageProcessState.SUCCESS) {
          successfulUploads.push({
            name: filesToUpload[index].name,
            url: res.fileRef,
          });
        } else {
          storageTaskFailed = true;
        }
      });
      if (storageTaskFailed) {
        // If some upload fails, we should stop the saving process
        // and delete the files that were uploaded (if there is any)
        if (attachmentsRefs.length) {
          const filesToDelete = successfulUploads.map(({ url }) => ({ storagePath: url }));
          await deleteAttachments(filesToDelete);
        }
      }
      attachmentsRefs.push(...successfulUploads);
    } catch (error) {
      storageTaskFailed = true;
      errorMsg = error.message || error;
    } finally {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      // eslint-disable-next-line no-unsafe-finally
      return {
        attachmentsRefs,
        storageTaskFailed,
        errorMsg,
      };
    }
  }, [
    fileAttachments,
    coachId,
    uploadAttachments,
    deleteAttachments,
    isComponentMountedRef,
    selectedWeek,
  ]);

  const getAttachmentRefs = useCallback(async () => {
    let attachments = [];
    let error = false;
    let errorMessage = '';
    if (fileAttachments.length) {
      const { storageTaskFailed, attachmentsRefs, errorMsg } = await uploadMessageAttachments();
      if (storageTaskFailed) {
        error = true;
        errorMessage = errorMsg;
      }
      attachments = attachmentsRefs;
    }
    return {
      attachments,
      error,
      errorMessage,
    };
  }, [
    fileAttachments,
    uploadMessageAttachments,
  ]);

  const handleSave = useCallback(async () => {
    if (!message) {
      showToast(texts.messageRequiredToEnable, { error: true });
      return;
    }

    const { attachments, error, errorMessage } = await getAttachmentRefs();
    if (error) {
      showToast(format(texts.uploadError, { error: errorMessage }), { error: true });
      return;
    }

    if (isComponentMountedRef.current) {
      setIsReady(false);
    }

    const newWeeklyMessaging = [
      ...coachConfig.clientJourneyWeeklyMessaging.filter(({ week }) => week !== selectedWeek),
      {
        week: selectedWeek,
        message,
        attachments,
        messageSendDay,
        updatedAt: new Date(),
        updatedBy: userId,
        isEnabled,
        messageSendTime: moment(messageSendTime).format('HH:mm'),
      },
    ];

    await coachConfig.setClientJourneyWeeklyMessaging(newWeeklyMessaging);

    if (isComponentMountedRef.current) {
      setIsReady(true);
    }
    logCoachingActivity(CoachingActivity.UPDATED_CLIENT_JOURNEY_WEEKLY_MESSAGING);
    showToast(texts.messageSaved);
  }, [
    getAttachmentRefs,
    showToast,
    coachConfig,
    selectedWeek,
    message,
    userId,
    isEnabled,
    isComponentMountedRef,
    logCoachingActivity,
    messageSendDay,
    messageSendTime,
  ]);

  const isError = characterCount > MAX_CHARACTERS_COUNT;

  return (
    <StyledWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>
      <ContentWrapper>
        <InputContainer>
          <InputLabel>{texts.weekOfClientJourney}</InputLabel>
          <Select
            value={selectedWeek}
            onChange={(event) => setSelectedWeek(event.target.value)}
            options={cohortOptions}
          />
        </InputContainer>
        <InputContainer>
          <InputLabel>{texts.enableMessaging}</InputLabel>
          <RadioButtonGroup
            options={ClientWeeklyJourneyMessagingToggleOptions}
            selectedOption={isEnabled ? ToggleOption.ENABLE : ToggleOption.DISABLE}
            onOptionChange={(value) => setIsEnabled(value === ToggleOption.ENABLE)}
          />
        </InputContainer>
        <InputContainer>
          <InputLabel>{texts.message}</InputLabel>
          <StyledTextField
            value={message}
            onChange={handleMessageTextChange}
            placeholder={texts.defaultMessage}
            error={isError}
            helperText={format(texts.characterCount, {
              count: characterCount,
              maxCount: MAX_CHARACTERS_COUNT,
            })}
            multiline
            maxRows={18}
          />
        </InputContainer>
        {dayOptions.length && (
          <InputContainer>
            <InputLabel>{texts.messageSendDayOfWeek}</InputLabel>
            <Select
              value={messageSendDay}
              onChange={(event) => setMessageSendDay(event.target.value)}
              options={dayOptions}
            />
          </InputContainer>
        )}
        <InputContainer>
          <InputLabel>{texts.messageSendTime}</InputLabel>
          <TimePicker
            value={messageSendTime}
            onChange={(newValue) => setMessageSendTime(newValue)}
            minutesStep={5}
            renderInput={(params) => (
              <StyledTimeField
                {...params}
                helperText={texts.timePickerHelperText}
                error={false}
              />
            )}
          />
        </InputContainer>
        <WeeklyMessageAttachmentContainer
          fileAttachments={fileAttachments}
          setFileAttachments={setFileAttachments}
          isUploading={isUploading}
        />
        <ButtonContainer>
          <SaveButton
            variant="contained"
            onClick={handleSave}
            disabled={isError}
            startIcon={<SaveIcon />}
          >
            {texts.saveButton}
          </SaveButton>
        </ButtonContainer>
      </ContentWrapper>
      <LoadingOverlay isLoading={!isReady} />
    </StyledWrapper>
  );
};

ClientWeeklyJourneyMessaging.propTypes = {
  cohortOptions: PropTypes.array.isRequired,
  dayOptions: PropTypes.array,
};

ClientWeeklyJourneyMessaging.defaultProps = {
  dayOptions: [],
};

export default compose(
  observer,
)(ClientWeeklyJourneyMessaging);
